import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MaskedLines, Overline, PrimaryButton, EASE } from "./shared";
import { LightArt } from "./LightArt";

const GROUPS = [
    { key: "space", label: "Space", options: ["Home", "Hotel", "Office", "Restaurant", "Retail", "Showroom", "Public Space", "Other"] },
    { key: "style", label: "Style", options: ["Minimal", "Organic", "Futuristic", "Luxury", "Sculptural", "Experimental"] },
    { key: "experience", label: "Experience", options: ["Light", "Motion", "Interaction", "Sound", "Combination"] },
    { key: "scale", label: "Scale", options: ["Small", "Medium", "Architectural"] },
];

const ART_BY_STYLE = { Minimal: "grid", Organic: "wave", Futuristic: "burst", Luxury: "rings", Sculptural: "rods", Experimental: "halo" };
const HUE_BY_EXPERIENCE = { Light: "warm", Motion: "mixed", Interaction: "cool", Sound: "mixed", Combination: "mixed" };
const NAME_BY_STYLE = { Minimal: "Quiet Grid", Organic: "Tideform", Futuristic: "Helix Array", Luxury: "Halo No. 1", Sculptural: "Slow Column", Experimental: "Strange Bloom" };

const Chip = ({ active, onClick, children, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`border px-5 py-3 font-mono-tech text-[10px] uppercase tracking-[0.25em] transition-colors duration-500 ${
            active ? "border-white bg-white text-black" : "border-white/20 text-white/50 hover:border-white/50 hover:text-white"
        }`}
    >
        {children}
    </button>
);

export const Configurator = ({ onCreate }) => {
    const [sel, setSel] = useState({ space: "Hotel", style: "Sculptural", experience: "Combination", scale: "Architectural" });
    const done = sel.space && sel.style && sel.experience && sel.scale;
    const conceptName = NAME_BY_STYLE[sel.style] || "Untitled Motion";
    const vision = `${sel.scale === "Architectural" ? "An" : "A"} ${sel.scale.toLowerCase()} ${sel.style.toLowerCase()} installation for a ${sel.space.toLowerCase()} — centred on ${sel.experience.toLowerCase()}.`;

    return (
        <section data-testid="configurator-section" className="relative border-t border-white/5 py-32 md:py-48">
            <div className="mx-auto max-w-[1600px] px-6 md:px-12">
                <Overline>The Configurator</Overline>
                <h2 className="mt-10 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
                    <MaskedLines lines={["Let's imagine", "your space."]} />
                </h2>
                <div className="mt-20 grid gap-16 lg:grid-cols-2 lg:gap-24">
                    <div className="space-y-12">
                        {GROUPS.map((g) => (
                            <div key={g.key}>
                                <p className="mb-5 font-mono-tech text-[10px] tracking-[0.35em] text-white/40">{g.label.toUpperCase()}</p>
                                <div className="flex flex-wrap gap-3">
                                    {g.options.map((o) => (
                                        <Chip
                                            key={o}
                                            testId={`configurator-${g.key}-${o.toLowerCase().replace(/\s/g, "-")}`}
                                            active={sel[g.key] === o}
                                            onClick={() => setSel({ ...sel, [g.key]: o })}
                                        >
                                            {o}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col">
                        <div className="relative min-h-[42vh] flex-1 border border-white/10">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${sel.style}-${sel.experience}`}
                                    className="absolute inset-0"
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.8, ease: EASE }}
                                >
                                    <LightArt variant={ART_BY_STYLE[sel.style]} hue={HUE_BY_EXPERIENCE[sel.experience]} className="h-full w-full" />
                                </motion.div>
                            </AnimatePresence>
                            <span className="absolute left-5 top-5 font-mono-tech text-[10px] tracking-[0.35em] text-white/40">CONCEPT PREVIEW</span>
                        </div>
                        <AnimatePresence mode="wait">
                            {done && (
                                <motion.div
                                    key={vision}
                                    data-testid="configurator-result"
                                    className="mt-8"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7, ease: EASE }}
                                >
                                    <p className="font-mono-tech text-[10px] tracking-[0.35em] text-[#FFE8B6]/70">CONCEPT — “{conceptName.toUpperCase()}”</p>
                                    <p className="mt-4 text-lg font-light leading-relaxed text-white/75">{vision}</p>
                                    <div className="mt-8">
                                        <PrimaryButton testId="configurator-create-btn" onClick={() => onCreate(vision)}>
                                            Create my installation
                                        </PrimaryButton>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
